// ============================================================
// TargetGearArmour - the Target Gear tab's armour card
// ============================================================
// One row per location (or vehicle facing): the current AP, the effective
// AP a hit meets when natural armour, machine or cover add to it, and the
// original when it was edited in combat. A row opens the armour editor.
// A vehicle also shows which facing is in play and its d10 hit table.

import { useMemo, type CSSProperties } from 'react';
import type { Target } from '../types';
import type { BodyConfig } from '../engine/bodyConfig';
import { effectiveVehicleFacing } from '../engine/vehicleFacing';
import { activatable } from '../utils/keyActivation';
import { armourLocations, armourRow } from './targetGear';
import styles from '../styles/TargetPanel.module.css';

interface Props {
  target: Target;
  bodyConfig: BodyConfig;
  isVehicle: boolean;
  onEditArmour: (locId: string) => void;
  locked: boolean;
}

export function TargetGearArmour({ target, bodyConfig, isVehicle, onEditArmour, locked }: Props) {
  const locations = useMemo(() => armourLocations(isVehicle, bodyConfig), [bodyConfig, isVehicle]);
  const activeFacing = isVehicle ? effectiveVehicleFacing(target) : null;
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>ARMOUR{activeFacing ? ` (${activeFacing.toUpperCase()} FACING)` : ''}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {locations.map(({ id, label }) => (
          <ArmourLine
            key={id}
            label={label}
            row={armourRow(target, id, bodyConfig)}
            isActiveFacing={id === activeFacing}
            onEdit={locked ? null : () => onEditArmour(id)}
          />
        ))}
      </div>
      {activeFacing && <VehicleNotes facing={activeFacing} bodyConfig={bodyConfig} />}
    </div>
  );
}

interface ArmourLineProps {
  label: string;
  row: ReturnType<typeof armourRow>;
  isActiveFacing: boolean;
  /** Opens the editor for this location; null when locked. */
  onEdit: (() => void) | null;
}

function ArmourLine({ label, row, isActiveFacing, onEdit }: ArmourLineProps) {
  const { current, orig, changed, effectiveAP } = row;
  return (
    <div
      style={lineStyle(changed, isActiveFacing, onEdit !== null)}
      title={onEdit ? `Click to edit ${label} armour` : undefined}
      {...(onEdit ? activatable(onEdit) : {})}
    >
      <span style={{ flex: 1, color: isActiveFacing ? 'var(--accent)' : 'var(--text-muted)' }}>{label}</span>
      <span
        style={{
          fontWeight: 'bold',
          color: changed ? 'var(--warn)' : 'var(--accent)',
          minWidth: 24,
          textAlign: 'right',
        }}
      >
        {current}
      </span>
      {effectiveAP !== current && (
        <span className={styles.effectiveAP} title="Effective AP (base + natural + machine + cover)">
          ({effectiveAP})
        </span>
      )}
      {changed && <span style={{ fontSize: 9, color: 'var(--text-dim)', marginLeft: 6 }}>(was {orig})</span>}
    </div>
  );
}

/** Changed (amber) outranks the active vehicle facing (gold) for the row's highlight. */
function lineStyle(changed: boolean, isActiveFacing: boolean, editable: boolean): CSSProperties {
  const highlight = changed ? 'changed' : isActiveFacing ? 'facing' : 'plain';
  return {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 6px',
    background: LINE_BACKGROUND[highlight],
    borderRadius: 'var(--radius-sm)',
    border: LINE_BORDER[highlight],
    cursor: editable ? 'pointer' : 'default',
    fontSize: 11,
  };
}

const LINE_BACKGROUND = { changed: 'var(--accent-dim)', facing: 'var(--bg-card)', plain: 'var(--bg-elevated)' };
const LINE_BORDER = {
  changed: '1px solid var(--warn)',
  facing: '1px solid var(--accent-dim)',
  plain: '1px solid transparent',
};

/** Which facing every hit uses, and the d10 table that picks the crit location. */
function VehicleNotes({ facing, bodyConfig }: { facing: string; bodyConfig: BodyConfig }) {
  return (
    <>
      <div style={{ fontSize: 9, color: 'var(--accent)', fontStyle: 'italic', marginTop: 4, padding: '0 6px' }}>
        All hits use {facing} facing AP. Location determines crit table.
      </div>
      <div style={{ marginTop: 6, padding: '0 6px' }}>
        <div style={{ fontSize: 9, color: 'var(--text-dim)', marginBottom: 2, fontWeight: 'bold' }}>
          HIT LOCATIONS (d10)
        </div>
        {bodyConfig.locations.map((loc) => (
          <div
            key={loc.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 9,
              color: 'var(--text-faint)',
              padding: '1px 0',
            }}
          >
            <span>{loc.name}</span>
            <span>
              {loc.min}–{loc.max}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
